

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  Rating,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Swal from 'sweetalert2';
import Confetti from 'react-confetti';

import Logo1 from "../landpageimage1/destinationlogo.png"
import Logo2 from "../../src/landpageimage/TDTLLogo.png"
import Logo3 from '../landpageimage1/mitlogo.jpg'
import Logo4 from '../landpageimage1/startuplogo.png'

const themeColors = {
  primary: "#FF6B00",
  secondary: "#FF9D45",
  gradient: "linear-gradient(135deg, #FF6B00 0%, #FF9D45 100%)",

  lightOrange: "#FFF3E0",
}

const StyledPaper = styled(Paper)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "90%",
  maxWidth: "1200px",
  margin: "0 auto",
  borderRadius: "24px",
  padding: "16px",
  backgroundColor: themeColors.white,
  boxShadow: "0 20px 40px rgba(255, 107, 0, 0.15)",
  overflow: "hidden",
}))

const StyledButton = styled(Button)(() => ({
  background: themeColors.gradient,
  color: 'white',
  padding: '8px 16px',
  borderRadius: '16px',
  fontWeight: 600,
  '&:hover': {
    background: '#1E88E5',
  },
}));

const LogoContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  padding: '16px 0',
  marginBottom: '16px',
}));

const LogoImage = styled('img')(() => ({
  maxWidth: '100px',
  maxHeight: '80px',
  objectFit: 'contain',
}));

export default function FeedbackForm() {
  const [feedback, setFeedback] = useState({
    email: '',
    presentationRating: 5,
    overallScore: '',
  });

  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [projectDetails, setProjectDetails] = useState({ title: '', description: '', id: '' });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const title = urlParams.get('title') || '';
    const description = urlParams.get('description') || '';
    const id = urlParams.get('id') || '';

    setProjectDetails({ title, description, id });

    setTimeout(() => setShowConfetti(false), 6000);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback((prevFeedback) => ({
      ...prevFeedback,
      [name]: value,
    }));
  };

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowConfetti(true);

    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get('id');

    // Validate Overall Score
    if (isNaN(feedback.overallScore) || feedback.overallScore < 0 || feedback.overallScore > 100) {
      Swal.fire('Error', 'Overall score must be between 0 and 100.', 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://puneexpobackend.tdtlworld.com/api/feedback/${projectId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idea: projectId,
          email: feedback.email,
          presentation_rating: feedback.presentationRating,
          overall_score: Number(feedback.overallScore), 
        }),
      });

      if (response.ok) {
        Swal.fire('Thank You!', 'Your feedback has been submitted successfully.', 'success');
        setTimeout(() => {
          setShowConfetti(false);
          window.location.href = 'https://lens.google.com/';
        }, 5000);
      } else {
        const errorData = await response.json();
        Swal.fire('Error', errorData.message || 'Failed to submit feedback.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'An unexpected error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: themeColors.background,
        padding: '16px',
        flexDirection: 'column',
      }}
    >
      {showConfetti && <Confetti />}
      
      {/* Logos at the top */}
      <LogoContainer>
        <LogoImage src={Logo1} alt="Logo 1" />
        <LogoImage src={Logo2} alt="Logo 2" />
        <LogoImage src={Logo3} alt="Logo 3" />
        <LogoImage src={Logo4} alt="Logo 4" />
      </LogoContainer>

      <StyledPaper>
        <Typography variant="h5" fontWeight="bold" color="primary" textAlign="center">
          Project Feedback Form
        </Typography>
        <Typography variant="body1" color="textSecondary" textAlign="center" paragraph sx={{ color: '#2196F3', fontWeight: 'bold' }}>
          Idea Name: {projectDetails.title}
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center" paragraph sx={{ color: '#6633EE', fontWeight: 'bold' }}>
          Idea Description: {projectDetails.description}
        </Typography>

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Grid container spacing={3}>
            {/* Email Field */}
            <Grid item xs={12}>
              <TextField
                label="Email"
                fullWidth
                name="email"
                value={feedback.email}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Golden Star Rating (Out of 10) */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Rate the Presentation (Out of 10 stars)</Typography>
              <Rating
                name="presentationRating"
                value={feedback.presentationRating}
                onChange={(event, newValue) => setFeedback({ ...feedback, presentationRating: newValue })}
                max={10}
                precision={1}
              />
            </Grid>

            {/* Overall Score (Manual Input) */}
            <Grid item xs={12}>
            <TextField
  label="Overall Score (0-100)"
  fullWidth
  name="overallScore"
  type="number"
  inputProps={{ min: 0, max: 100, pattern: "^(100|[0-9]{1,2})$" }} // Regex validation
  value={feedback.overallScore}
  onChange={(e) => {
    const value = e.target.value;
    const isValid = /^(100|[0-9]{1,2})$/.test(value); // Regex validation for 0-100

    if (isValid || value === '') {
      setFeedback({ ...feedback, overallScore: value });
    }
  }}
  required
  error={feedback.overallScore !== '' && !/^(100|[0-9]{1,2})$/.test(feedback.overallScore)}
  helperText={feedback.overallScore !== '' && !/^(100|[0-9]{1,2})$/.test(feedback.overallScore) ? "Enter a valid score between 0-100" : ""}
/>

            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <StyledButton type="submit" fullWidth disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Feedback'}
              </StyledButton>
            </Grid>
          </Grid>
        </form>
      </StyledPaper>
    </Box>
  );
}
